import { Download, ShoppingBasket, Palette, Link2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { DEFAULT_HEIGHT } from "../../lib/api";
import { statusLabel } from "../DuckCard";
import { RenderChips } from "./RenderChips";

const Fact = ({ label, testId, children }) => (
  <div className="min-w-0">
    <dt className="text-[10px] font-mono2 uppercase tracking-widest text-muted-foreground">{label}</dt>
    <dd data-testid={testId} className="mt-1 text-sm font-semibold">{children}</dd>
  </div>
);

const Swatches = ({ palette, rowId }) =>
  palette?.length ? (
    <span className="flex flex-wrap gap-1" data-testid={`duck-swatches-${rowId}`}>
      {palette.map((p, k) => (
        <span key={`${p.hex}-${k}`} title={p.name || p.hex} className="h-4 w-4 rounded-full border border-black/10 shadow-inner" style={{ backgroundColor: p.hex }} />
      ))}
    </span>
  ) : (
    <span className="text-muted-foreground font-normal">Not painted yet</span>
  );

const Act = ({ testId, onClick, icon: Icon, label, variant = "secondary", className = "" }) => (
  <Button size="sm" variant={variant} data-testid={testId} onClick={onClick} className={`h-8 rounded-full gap-1 text-xs ${className}`}>
    <Icon className="h-3.5 w-3.5" /> {label}
  </Button>
);

// What an opened list row shows: AI render chips, colours, slots, print height, status, and the duck's actions.
export const DuckDetails = ({ c, rowId, actions, cover = null }) => (
  <div data-testid={`duck-details-${rowId}`} className="border-t border-border/70 px-3 py-3 grid gap-4 animate-in fade-in slide-in-from-top-1 duration-200">
    <dl className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      <div className="col-span-2 sm:col-span-1">
        <dt className="text-[10px] font-mono2 uppercase tracking-widest text-muted-foreground mb-1">AI renders</dt>
        <RenderChips c={c} rowId={rowId} />
      </div>
      <Fact label="Colours"><Swatches palette={c.palette} rowId={rowId} /></Fact>
      <Fact label="Slots" testId={`duck-slots-${rowId}`}>{c.slots}</Fact>
      <Fact label="Print height" testId={`duck-height-${rowId}`}>
        {c.print_height ? `${c.print_height} mm` : <>{DEFAULT_HEIGHT} mm <span className="font-normal text-muted-foreground">(default)</span></>}
      </Fact>
      <Fact label="Status" testId={`duck-status-${rowId}`}>{statusLabel(c)}{c.ordered ? " · ordered" : ""}</Fact>
    </dl>
    <div className="flex flex-wrap gap-1.5">
      {c.has_model && <Act testId={`duck-row-3mf-${rowId}`} onClick={() => actions.download3mf(c)} icon={Download} label="3MF" />}
      {c.has_model && <Act testId={`duck-row-stl-${rowId}`} variant="outline" onClick={() => actions.downloadStl(c)} icon={Download} label="STL" />}
      {c.has_model && <Act testId={`duck-row-basket-${rowId}`} variant="outline" onClick={() => actions.basketAdd([c.id])} icon={ShoppingBasket} label="Basket" />}
      {c.has_model && <Act testId={`duck-row-repaint-${rowId}`} variant="ghost" onClick={() => actions.repaint(c)} icon={Palette} label="Re-paint" />}
      {c.has_model && <Act testId={`duck-row-share-${rowId}`} variant="ghost" onClick={() => actions.share(c)} icon={Link2} label="Share" />}
      {cover}
      <Act testId={`duck-row-delete-${rowId}`} variant="ghost" className="text-destructive" onClick={() => actions.del(c.id)} icon={Trash2} label="Delete" />
    </div>
  </div>
);
